import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, ArrowRight, Phone, Mail } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { articlesData } from '../data/articlesData';
import './ArticlePage.css';

// Texte enrichi : liens markdown [libellé](/chemin) et gras **texte**.
// Rendu en éléments React (jamais dangerouslySetInnerHTML).
const RICH_TOKEN = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g;

function renderRichText(text) {
  if (text === undefined || text === null) return null;
  return String(text).split(RICH_TOKEN).map((part, i) => {
    if (!part) return null;

    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      const [, label, href] = link;
      return href.startsWith('/')
        ? <Link key={i} to={href} className="article-link">{label}</Link>
        : <a key={i} href={href} className="article-link" target="_blank" rel="noopener noreferrer">{label}</a>;
    }

    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) return <strong key={i}>{bold[1]}</strong>;

    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// Version texte brut (pour le JSON-LD, qui ne doit pas contenir de markdown).
function stripRich(text) {
  return String(text || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1');
}

export default function ArticlePage({ t, language }) {
  const { slug } = useParams();
  const article = articlesData.find(a => a.slug === slug);
  const ta = t?.articlePage || {};
  const isEN = language === 'en';

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  // Suggestions par pertinence : même catégorie d'abord, puis les plus récents.
  const relatedArticles = articlesData
    .filter(a => a.slug !== slug)
    .sort((x, y) => {
      const sameCategory = (a) => (a.category === article.category ? 1 : 0);
      if (sameCategory(y) !== sameCategory(x)) return sameCategory(y) - sameCategory(x);
      return String(y.dateISO || '').localeCompare(String(x.dateISO || ''));
    })
    .slice(0, 2);

  const baseUrl = 'https://dp-architecture.com';
  const pageUrl = `${baseUrl}/actualites/${article.slug}`;
  const imageUrl = `${baseUrl}${article.image}`;

  const title = isEN ? (article.titleEN || article.title) : article.title;
  const excerpt = isEN ? (article.excerptEN || article.excerpt) : article.excerpt;
  const content = isEN ? (article.contentEN || article.content) : article.content;
  const date = isEN ? (article.dateEN || article.date) : article.date;
  const category = isEN ? (article.categoryEN || article.category) : article.category;
  const faq = (isEN ? (article.faqEN || article.faq) : article.faq) || [];

  // JSON-LD : Article
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': pageUrl,
    headline: article.title,
    description: article.seoDescription,
    datePublished: article.dateISO,
    dateModified: article.dateModifiedISO || article.dateISO,
    url: pageUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: 1200,
      height: 630
    },
    author: {
      '@type': 'Person',
      name: 'Daniela Parito Sorrenti',
      jobTitle: 'Architecte',
      url: `${baseUrl}/agency`
    },
    publisher: {
      '@type': 'Organization',
      name: 'DP Architecture – Daniela Parito',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/images/logo/dplogo.webp`,
        width: 200,
        height: 60
      }
    },
    about: {
      '@type': 'Service',
      name: 'Architecture – Hérault',
      areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' }
    },
    keywords: article.seoKeywords,
    inLanguage: 'fr-FR'
  };

  // JSON-LD : BreadcrumbList
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Actualités', item: `${baseUrl}/news` },
      { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl }
    ]
  };

  // JSON-LD : FAQPage — construit depuis la FAQ VISIBLE, dans la langue affichée
  const faqSchema = faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: stripRich(item.a) }
    }))
  } : null;

  return (
    <div className="article-page">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      {faqSchema && <JsonLd data={faqSchema} />}
      <SEO
        title={article.seoTitle}
        description={article.seoDescription}
        keywords={article.seoKeywords}
        image={`https://dp-architecture.com${article.image}`}
        type="article"
      />

      {/* ===== HERO ===== */}
      <section
        className="article-hero"
        style={{ backgroundImage: `url(${article.image})` }}
      >
        <div className="article-hero-overlay" />
        <div className="article-hero-content">
          <Link to="/news" className="article-back-link">
            <ArrowLeft size={16} />
            {ta.backToNews || 'Retour aux actualités'}
          </Link>
          <div className="article-hero-meta">
            <span className="article-hero-date">
              <Calendar size={14} />
              {date}
            </span>
            <span className="article-hero-category">
              <Tag size={14} />
              {category}
            </span>
          </div>
          <h1 className="article-hero-title">{title}</h1>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <div className="article-layout">
        <article className="article-body">
          <ScrollReveal>
            <p className="article-excerpt">{excerpt}</p>
          </ScrollReveal>

          {content.map((block, idx) => (
            <ScrollReveal key={idx} delay={idx * 0.05}>
              {block.type === 'heading' ? (
                <h2 className="article-heading">{block.text}</h2>
              ) : block.type === 'subheading' ? (
                <h3 className="article-subheading">{renderRichText(block.text)}</h3>
              ) : block.type === 'list' ? (
                <ul className="article-list">
                  {(block.items || []).map((item, i) => (
                    <li key={i}>{renderRichText(item)}</li>
                  ))}
                </ul>
              ) : block.type === 'image' ? (
                <figure className="article-figure">
                  <img
                    src={block.src}
                    alt={block.alt || ''}
                    className="article-figure-img"
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
              ) : (
                <p className="article-paragraph">{renderRichText(block.text)}</p>
              )}
            </ScrollReveal>
          ))}

          {/* FAQ de l'article (visible + FAQPage JSON-LD aligné sur la langue affichée) */}
          {faq.length > 0 && (
            <section className="article-faq">
              <ScrollReveal>
                <h2 className="article-heading">
                  {ta.faqTitle || (isEN ? 'Frequently asked questions' : 'Questions fréquentes')}
                </h2>
              </ScrollReveal>
              {faq.map((item, i) => (
                <ScrollReveal key={i} delay={i * 0.05}>
                  <details className="article-faq-item" name="articlefaq">
                    <summary className="article-faq-question">{item.q}</summary>
                    <div className="article-faq-answer">{renderRichText(item.a)}</div>
                  </details>
                </ScrollReveal>
              ))}
            </section>
          )}

          {/* CTA inline */}
          <ScrollReveal>
            <div className="article-inline-cta">
              <p>{ta.inlineCtaText || "Vous avez un projet dans l'Hérault ?"}</p>
              <Link to="/contact" className="article-cta-btn">
                {ta.inlineCtaBtn || 'Contactez DP Architecture'}
                <ArrowRight size={16} />
              </Link>
            </div>
          </ScrollReveal>
        </article>

        {/* ===== SIDEBAR ===== */}
        <aside className="article-sidebar">
          {/* Contact rapide */}
          <div className="sidebar-card sidebar-contact">
            <h3 className="sidebar-title">{ta.sidebarContactTitle || "Votre projet dans l'Hérault"}</h3>
            <p className="sidebar-text">
              {ta.sidebarContactText || "Daniela Parito Architecture intervient dans tout le département de l'Hérault."}
            </p>
            <a href="tel:+33614902513" className="sidebar-contact-link">
              <Phone size={16} />
              06 14 90 25 13
            </a>
            <a href="mailto:daniela.parito@gmail.com" className="sidebar-contact-link">
              <Mail size={16} />
              daniela.parito@gmail.com
            </a>
            <Link to="/contact" className="sidebar-cta">
              {ta.sidebarQuote || 'Devis gratuit'}
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Articles liés */}
          {relatedArticles.length > 0 && (
            <div className="sidebar-card sidebar-related">
              <h3 className="sidebar-title">{ta.sidebarRelatedTitle || 'À lire aussi'}</h3>
              <div className="sidebar-related-list">
                {relatedArticles.map(related => {
                  const relatedTitle = isEN ? (related.titleEN || related.title) : related.title;
                  const relatedCategory = isEN ? (related.categoryEN || related.category) : related.category;
                  return (
                    <Link
                      key={related.slug}
                      to={`/actualites/${related.slug}`}
                      className="sidebar-related-item"
                    >
                      <img
                        src={related.image}
                        alt={relatedTitle}
                        className="sidebar-related-img"
                        loading="lazy"
                      />
                      <div className="sidebar-related-info">
                        <span className="sidebar-related-category">{relatedCategory}</span>
                        <p className="sidebar-related-title">{relatedTitle}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Villes */}
          <div className="sidebar-card sidebar-cities">
            <h3 className="sidebar-title">{ta.sidebarCitiesTitle || 'Nous intervenons à'}</h3>
            <ul className="sidebar-cities-list">
              {[
                { label: 'Montpellier', href: '/' },
                { label: 'Béziers', href: '/architecte-beziers' },
                { label: 'Sète', href: '/architecte-sete' },
                { label: 'Mauguio', href: '/architecte-mauguio' },
                { label: 'Baillargues', href: '/architecte-baillargues' },
                { label: 'Florensac', href: '/architecte-florensac' }
              ].map(city => (
                <li key={city.href}>
                  <Link to={city.href} className="sidebar-city-link">
                    {city.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
